import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import useSEO from '@/hooks/useSEO';
import { 
  Mic, Sparkles, FileText, Share2, Clock, Zap, 
  Check, ArrowRight, PlayCircle, Target, Timer,
  Quote, Twitter, Instagram, BookOpen, ListChecks, Download,
  Sliders, PenTool, ChevronDown, Copy, Headphones, Hash,
  MessageSquare, Mail, Linkedin, List, Search, FileArchive, Video
} from 'lucide-react';
import RealOutputExamplesSection from '@/components/RealOutputExamplesSection';
import HomeBlogPreview from '@/components/HomeBlogPreview';

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// COMPLETE OUTPUT INVENTORY - Real episode: My First Million × Manish Pabrai
// "How to turn $10K into $1M" — content extracted from a real EpisodeKit run
const fullOutputData = {
  label: "Generated from a real podcast episode (see demo above). This entire page is based on one real episode processed in EpisodeKit.",
  episodeTitle: "How to Turn $10K Into $1M — with Manish Pabrai",
  duration: "1h 12m",
  
  // 1. TRANSCRIPT (with timestamps)
  transcript: {
    preview: [
      { time: "00:00:02", text: "How would I take 10K and turn it into a million?" },
      { time: "00:00:11", text: "We don't need to know many things about many things. We need to know a lot about a little." },
      { time: "00:00:27", text: "Buffett always says, the most important question to ask is: and then what?" },
      { time: "00:00:37", text: "Usually the best ideas, when you finally figure that out, they're very simple." },
      { time: "00:00:45", text: "You should be able to explain your thesis of a stock in four or five sentences to a 10-year-old." }
    ],
    wordCount: "11,420 words",
    accuracy: "99%"
  },

  // 2. SHOW NOTES (full structure)
  showNotes: {
    summary: "Sean welcomes back investor Manish Pabrai to break down how to turn $10K into $1M. They cover concentrated knowledge over broad surface-level investing, why Berkshire Hathaway can act as a personal index, how to spot market anomalies, and the discipline of asking 'and then what?' before every big move.",
    introduction: "A 100x challenge: how would Manish coach a beginner to grow $10K into $1M? The answer starts with patience, a day job, and learning to wait for ideas that hit you over the head.",
    keyTakeaways: [
      "Deep knowledge in a few areas beats shallow knowledge across many",
      "Treat Berkshire Hathaway as a personal long-term index when the S&P feels overheated",
      "The best opportunities are anomalies — rare, simple, and obvious once you see them",
      "Ask 'and then what?' to stress-test every investment thesis",
      "You should be able to explain a stock in 4–5 sentences to a 10-year-old"
    ],
    guestBios: ["Manish Pabrai — value investor, author, and founder of Pabrai Investment Funds"],
    resourcesMentioned: [
      "Berkshire Hathaway",
      "Frontline Ltd. (shipping)",
      "Moody's Manual",
      "Warren Buffett's 2022 letter"
    ],
    conclusion: "Don't chase. Wait for the 2x4 that hits you over the head — then size up."
  },

  // 3. CHAPTERS (timestamps + descriptions)
  chapters: [
    { time: "00:00", title: "Transforming $10K into $1M", desc: "The 100x challenge and where to start" },
    { time: "08:40", title: "First & Second Order Thinking", desc: "The 'and then what?' question" },
    { time: "17:21", title: "Discovering Investment Anomalies", desc: "How rare opportunities appear" },
    { time: "26:02", title: "Simplicity in Investing", desc: "Explaining a thesis to a 10-year-old" },
    { time: "34:42", title: "The Narrow Circle of Competence", desc: "A lot about a little" },
    { time: "52:04", title: "Dakshina Foundation", desc: "Strategic philanthropy" },
    { time: "1:09:24", title: "Rakesh Jhunjhunwala's Strategy", desc: "India's most renowned investor" }
  ],

  // 4. BLOG POST (full structure)
  blogPost: {
    metaTitle: "Turning $10K Into $1M: A Patient Investor's Playbook",
    metaDescription: "Manish Pabrai breaks down how to grow $10K into $1M — through concentrated knowledge, market anomalies, and the discipline of asking 'and then what?'",
    keywords: ["investment strategies", "$10K to $1M", "Warren Buffett principles", "Manish Pabrai", "value investing"],
    content: `Most people think investing is about knowing more. Manish Pabrai disagrees.

"We don't need to know many things about many things. We need to know a lot about a little."

The path from $10K to $1M isn't about chasing the S&P 500 or jumping into the next hot stock. It's about keeping a day job, saving consistently, parking the default in something stable like Berkshire Hathaway — then waiting for the rare anomaly that "hits you in the head with a 2x4."

Buffett's most important question, Pabrai reminds us, isn't "is this cheap?" It's "and then what?" The investors who get rich aren't the ones with the most spreadsheets. They're the ones who can explain a stock in four or five sentences — to a 10-year-old.`
  },

  // 5. PROMO SNIPPETS (twitter, linkedin, newsletter)
  promoSnippets: {
    twitter: "🎙️ How do you turn $10K into $1M?\n\nManish Pabrai's answer: stop chasing. Wait for the 2x4 that hits you over the head — then size up.\n\nA masterclass on focus, patience, and Buffett's most underrated question:\n\n\"And then what?\"",
    linkedin: "The best investing advice on the internet right now is from Manish Pabrai on My First Million:\n\n\"We don't need to know many things about many things. We need to know a lot about a little.\"\n\nWhile most investors spread thin across 30 stocks, the ones compounding at 20%+ are doing the opposite — concentrated knowledge, simple theses, and the patience to wait years for a real anomaly.\n\nBuffett's question still wins: and then what?",
    newsletter: "This week: Manish Pabrai on the 100x challenge.\n\nIf you had $10K and wanted to grow it to $1M — what would you actually do?\n\nManish's playbook: keep your day job, treat Berkshire Hathaway as your default index, and wait for the rare opportunity that's so obvious you can explain it to a 10-year-old.\n\nIt's the most useful 70 minutes on investing we've recorded all year.\n\nListen now →"
  },

  // 6. BEST QUOTES (Pro+)
  bestQuotes: [
    {
      quote: "We don't need to know many things about many things. We need to know a lot about a little.",
      speaker: "Manish Pabrai",
      context: "On building a circle of competence"
    },
    {
      quote: "Usually the best ideas, when you finally figure that out, they're very simple. You should be able to explain your thesis in four or five sentences to a 10-year-old.",
      speaker: "Manish Pabrai",
      context: "On investing simplicity"
    },
    {
      quote: "The most important question to ask is: and then what?",
      speaker: "Manish Pabrai (quoting Buffett)",
      context: "On second-order thinking"
    }
  ],

  // 7. SEO TITLES (Pro+)
  seoTitles: [
    "How to Turn $10K Into $1M — Manish Pabrai's Full Playbook",
    "The 'And Then What?' Question That Made Buffett Billions",
    "Why Concentrated Knowledge Beats Diversification",
    "Manish Pabrai on Berkshire Hathaway as a Personal Index",
    "Patience and Anomalies: The Real Path to 100x Returns"
  ],

  // 8. TIKTOK CAPTIONS (Pro+)
  tiktokCaption: {
    hook: "How to turn $10K into $1M 💸",
    caption: "Manish Pabrai's 3-step playbook:\n\n1. Keep your day job. Save aggressively.\n2. Park the default in Berkshire Hathaway.\n3. Wait for the rare idea that hits you over the head — and size up.\n\nNo stock-picking. No trading. Just patience.",
    hashtags: "#investing #stocks #warrenbuffett #manishpabrai #myfirstmillion #wealthbuilding #financialfreedom",
    cta: "Save this before your next trade 🚀"
  },

  // 9. INSTAGRAM CAPTIONS (Pro+)
  instagramCaptions: {
    carousel: {
      caption: "Manish Pabrai on growing $10K into $1M:\n\n\"We don't need to know many things about many things. We need to know a lot about a little.\"\n\n→ Swipe for the full 3-step playbook every patient investor should know",
      hashtags: "#investing #valueinvesting #warrenbuffett #manishpabrai #wealthbuilding #financialfreedom #myfirstmillion"
    },
    story: {
      text: "$10K → $1M? 💸\n\nManish Pabrai's playbook is wild.\n\n👆 Tap to listen"
    },
    reels: {
      hook: "Buffett's most underrated question:",
      caption: "\"And then what?\"\n\nIt's the difference between a good investor and a great one. Listen to Manish Pabrai break it down.",
      cta: "Full episode in bio 🎧"
    }
  },

  // 10. TONE OPTIONS
  toneOptions: ["Professional", "Casual", "Educational", "Storytelling", "Technical"],

  // 11. CUSTOM INSTRUCTIONS EXAMPLE
  customInstructions: "Keep tone conversational and quote Manish directly. Mention Buffett, Berkshire Hathaway, and the 'and then what?' framework. Avoid generic finance jargon.",

  // 12. EXPORT OPTIONS
  exportFiles: [
    "transcript.md",
    "show-notes.md",
    "blog-post.md",
    "promo-snippets.md",
    "best-quotes.md",
    "seo-titles.md",
    "tiktok-captions.md",
    "instagram-captions.md",
    "chapters.md"
  ]
};

const Landing = () => {
  const [copiedItem, setCopiedItem] = useState(null);
  const [activeTab, setActiveTab] = useState('core');
  const location = useLocation();
  const tabsScrollRef = useRef(null);

  useSEO({
    title: 'EpisodeKit — Turn Podcasts & Videos Into 12+ Content Assets in Minutes',
    description: 'Transform podcasts and videos into transcripts, show notes, SEO blog posts, social posts, YouTube descriptions, chapters, quotes, and more — in minutes. Built for creators who want their podcast to actually rank on Google and YouTube.',
    path: '/',
    image: '/og/episodekit-og-home.jpg',
    index: true,
  });
  
  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const handleCopy = (text, itemName) => {
    // Safe copy method that works in all environments including preview
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      textArea.style.top = '-9999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedItem(itemName);
      setTimeout(() => setCopiedItem(null), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const scrollToOutput = () => {
    const element = document.getElementById('features');
    if (element) {
      const yOffset = -80; // Account for navbar
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const scrollToDemo = () => {
    const element = document.getElementById('demo-video');
    if (element) {
      const yOffset = -80;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const stats = [
    { value: '12+', label: 'Content Assets Generated', icon: Target },
    { value: '2+', label: 'Hours Saved Per Episode', icon: Clock },
    { value: '5', label: 'Tone Presets', icon: Zap },
    { value: '<5min', label: 'Processing Time', icon: Timer }
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="noise-overlay" />
      <Navbar />

      {/* HERO SECTION */}
      <section className="hero-glow pt-36 pb-20 px-6 md:px-12" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center max-w-4xl mx-auto"
            initial="initial"
            animate="animate"
            variants={stagger}
          >
            <motion.div 
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-8"
              variants={fadeIn}
            >
              <Headphones className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-medium">For podcasters who hate post-production</span>
            </motion.div>
            
            <motion.h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-unbounded font-bold leading-tight mb-6"
              variants={fadeIn}
            >
              One episode.{' '}
              <span className="bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] bg-clip-text" style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                12+ content assets
              </span>
              {' '}that drive traffic and grow your audience.
            </motion.h1>
            
            <motion.p 
              className="text-lg md:text-xl text-neutral-200 mb-4 max-w-3xl mx-auto leading-relaxed font-medium"
              variants={fadeIn}
            >
              Upload your podcast or video. EpisodeKit turns it into transcripts, show notes, blog posts, social captions, YouTube descriptions, SEO titles, chapters, quotes, and more — so your content is easier to find, rank, and publish everywhere.
            </motion.p>
            
            <motion.p 
              className="text-base text-neutral-400 mb-3 max-w-2xl mx-auto"
              variants={fadeIn}
            >
              Built for podcasters and YouTube creators who want more reach without doing post-production manually.
            </motion.p>
            
            <motion.p 
              className="text-sm text-neutral-600 mb-10 max-w-xl mx-auto"
              variants={fadeIn}
            >
              No prompts. No setup. No hidden features.
            </motion.p>
            
            <motion.div className="flex flex-wrap gap-4 justify-center mb-4" variants={fadeIn}>
              <Link to="/register">
                <Button className="btn-primary flex items-center gap-2 text-lg px-8 py-6 font-semibold" data-testid="hero-cta-btn">
                  Try Free
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Button 
                variant="ghost" 
                className="btn-secondary flex items-center gap-2 text-lg px-8 py-6"
                onClick={scrollToDemo}
                data-testid="hero-watch-demo-btn"
              >
                <PlayCircle className="w-5 h-5" />
                Watch Demo
              </Button>
            </motion.div>
            
            <motion.div className="flex justify-center mb-8" variants={fadeIn}>
              <button
                onClick={scrollToOutput}
                className="text-sm text-neutral-500 hover:text-neutral-300 transition-colors inline-flex items-center gap-1"
                data-testid="see-output-btn"
              >
                Or see the full output inventory
                <ChevronDown className="w-4 h-4" />
              </button>
            </motion.div>
            
            <motion.div 
              className="flex items-center justify-center gap-6 text-sm text-neutral-500"
              variants={fadeIn}
            >
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-500" />
                <span>No credit card</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-500" />
                <span>1 free episode/month</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-500" />
                <span>60 minutes included</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* PAIN BLOCK — between hero and demo */}
      <section className="py-16 px-6 md:px-12" data-testid="pain-section">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-unbounded font-bold mb-6">
            Most creators are invisible
          </h2>
          <p className="text-neutral-300 leading-relaxed mb-6">
            You publish once — and it dies.<br />
            No SEO. No traffic.<br />
            No repurposing. No growth.
          </p>
          <p className="text-neutral-400 leading-relaxed">
            EpisodeKit turns every episode into a complete content system — automatically.
          </p>
        </div>
      </section>

      {/* DEMO VIDEO SECTION */}
      <section
        id="demo-video"
        className="relative py-20 px-6 md:px-12 bg-[#070707]"
        data-testid="demo-video-section"
      >
        {/* subtle purple glow */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              'radial-gradient(60% 50% at 50% 0%, rgba(124,58,237,0.18), transparent 70%)'
          }}
        />
        <div className="relative max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-secondary uppercase text-xs tracking-widest mb-4 font-semibold">
              Product Demo
            </p>
            <motion.h2
              className="text-3xl md:text-4xl font-unbounded font-bold mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Watch EpisodeKit turn one episode into 12+ assets
            </motion.h2>
            <motion.p
              className="text-neutral-400 max-w-2xl mx-auto"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              See the full workflow: upload, processing, dashboard, and ready-to-publish outputs in just over two minutes.
            </motion.p>
          </div>

          <motion.div
            className="relative rounded-2xl overflow-hidden border border-neutral-800 bg-neutral-950 shadow-2xl shadow-primary/10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{
              boxShadow:
                '0 0 0 1px rgba(124,58,237,0.15), 0 30px 80px -20px rgba(124,58,237,0.35)'
            }}
          >
            <video
              controls
              preload="metadata"
              playsInline
              poster="/api/demo/episodekit-demo-poster.jpg"
              className="w-full h-auto block aspect-video bg-black"
              data-testid="demo-video-player"
            >
              <source src="/api/demo/episodekit-demo.mp4" type="video/mp4" />
              Sorry, your browser doesn&apos;t support embedded videos.
            </video>
          </motion.div>

          <p className="text-center text-xs text-neutral-600 mt-6">
            Recorded inside the real EpisodeKit dashboard. Processing steps are slightly sped up for a faster walkthrough.
          </p>
        </div>
      </section>

      {/* FULL OUTPUT INVENTORY SECTION */}
      <section id="features" className="py-24 px-6 md:px-12 bg-[#0A0A0A]" data-testid="full-output-section" data-nosnippet="">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <motion.p 
              className="text-secondary uppercase text-xs tracking-widest mb-4 font-semibold"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Complete Output Inventory
            </motion.p>
            <motion.h2 
              className="text-3xl md:text-4xl font-unbounded font-bold mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Turn One Episode Into a Complete Content System
            </motion.h2>
            <motion.p 
              className="text-neutral-400 max-w-2xl mx-auto mb-3"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Everything you need to publish, rank, and grow — from a single upload.
            </motion.p>
            <motion.p 
              className="text-neutral-400 max-w-2xl mx-auto mb-6"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              No editing. No manual work. No post-production.
            </motion.p>
            <motion.div 
              className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-900 rounded-full text-sm text-neutral-400 border border-neutral-800"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <Sparkles className="w-4 h-4 text-secondary" />
              <span>{fullOutputData.label}</span>
            </motion.div>
          </div>

          {/* Category Tabs */}
          <p className="text-center text-sm text-neutral-500 mb-3">
            <span className="md:hidden">Explore output type:</span>
            <span className="hidden md:inline">See exactly what your episode becomes:</span>
          </p>
          
          {/* Mobile: native select dropdown for clarity & accessibility */}
          <div className="md:hidden flex flex-col items-center mb-6 px-4">
            <div className="relative w-full max-w-sm">
              {/* ambient glow halo — very subtle slow pulse */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -inset-[2px] rounded-2xl bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] blur-md animate-ambient-glow"
              />
              <select
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
                data-testid="output-tab-select"
                aria-label="Explore output type"
                className="relative appearance-none w-full pl-5 pr-12 py-4 bg-neutral-900 border border-white/15 rounded-2xl text-white font-semibold text-base shadow-xl shadow-primary/15 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-white/25 transition-all duration-200 cursor-pointer"
              >
                <option value="core">Core Content</option>
                <option value="social">Social Media</option>
                <option value="seo">SEO &amp; Quotes</option>
                <option value="youtube">YouTube</option>
                <option value="controls">AI Controls</option>
              </select>
              <ChevronDown
                aria-hidden="true"
                strokeWidth={2.25}
                className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 w-[22px] h-[22px] text-white"
              />
            </div>
            <p className="text-sm font-medium text-white/70 mt-3">Tap to switch between output examples</p>
          </div>

          {/* Desktop: tab buttons */}
          <div className="hidden md:flex justify-center mb-6 px-2">
            <div className="relative w-auto">
              <div
                ref={tabsScrollRef}
                className="flex flex-wrap items-center gap-2 p-2 bg-neutral-900/50 backdrop-blur-sm border border-white/10 rounded-2xl shadow-lg shadow-black/20"
                role="tablist"
                data-testid="output-tabs"
              >
                {[
                  { id: 'core', label: 'Core Content', icon: FileText },
                  { id: 'social', label: 'Social Media', icon: Share2 },
                  { id: 'seo', label: 'SEO & Quotes', icon: Search },
                  { id: 'youtube', label: 'YouTube', icon: Video },
                  { id: 'controls', label: 'AI Controls', icon: Sliders }
                ].map(tab => {
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveTab(tab.id)}
                      data-testid={`output-tab-${tab.id}`}
                      style={{ minWidth: '120px', maxWidth: '140px' }}
                      className={`flex items-center justify-center gap-2 shrink-0 px-3 py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap transition-all duration-200 ease-out hover:brightness-110 active:scale-[0.97] ${
                        isActive
                          ? 'bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] text-white shadow-lg shadow-primary/30 ring-1 ring-white/20'
                          : 'bg-neutral-900/60 text-neutral-400 hover:text-neutral-100 hover:bg-neutral-800/80'
                      }`}
                    >
                      <tab.icon className="w-[18px] h-[18px] shrink-0" />
                      <span className="truncate">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Included outputs pill — small reminder that ZIP + downloads are part of every plan */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-neutral-900/60 border border-white/5 rounded-full text-xs text-neutral-400">
              <Download className="w-3 h-3" />
              <span>Includes ZIP export &amp; per-asset downloads on every plan</span>
            </div>
          </div>

          {/* CORE CONTENT TAB */}
          {activeTab === 'core' && (
            <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Transcript */}
              <motion.div className="card-base p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                      <Mic className="w-5 h-5 text-cyan-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Full Transcript</h3>
                      <p className="text-xs text-neutral-500">{fullOutputData.transcript.wordCount} • {fullOutputData.transcript.accuracy} accuracy</p>
                    </div>
                  </div>
                </div>
                <div className="bg-neutral-900/50 rounded-lg p-4 font-mono text-xs space-y-2 max-h-48 overflow-hidden relative">
                  {fullOutputData.transcript.preview.map((line, i) => (
                    <p key={i} className="text-neutral-300">
                      <span className="text-cyan-400">[{line.time}]</span> {line.text}
                    </p>
                  ))}
                  <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-neutral-900/50 to-transparent" />
                </div>
              </motion.div>

              {/* Show Notes */}
              <motion.div className="card-base p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <ListChecks className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Show Notes</h3>
                      <p className="text-xs text-neutral-500">Summary + Key Takeaways + Resources</p>
                    </div>
                  </div>
                  <button onClick={() => handleCopy(fullOutputData.showNotes.summary, 'shownotes')} className="flex items-center gap-1 px-2 py-1 bg-neutral-800 hover:bg-neutral-700 rounded text-xs text-neutral-400">
                    <Copy className="w-3 h-3" />
                    {copiedItem === 'shownotes' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="text-neutral-300 text-sm mb-3">{fullOutputData.showNotes.summary}</p>
                <div className="space-y-1.5">
                  {fullOutputData.showNotes.keyTakeaways.slice(0, 4).map((t, i) => (
                    <p key={i} className="text-xs text-neutral-400 flex items-start gap-2">
                      <Check className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                      {t}
                    </p>
                  ))}
                </div>
              </motion.div>

              {/* Chapters */}
              <motion.div className="card-base p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                    <List className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Chapters</h3>
                    <p className="text-xs text-neutral-500">{fullOutputData.chapters.length} chapters with timestamps</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {fullOutputData.chapters.map((ch, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <span className="text-cyan-400 font-mono text-xs w-12">{ch.time}</span>
                      <span className="text-neutral-300">{ch.title}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Blog Post */}
              <motion.div className="card-base p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-secondary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">SEO Blog Post</h3>
                      <p className="text-xs text-neutral-500">800-1200 words, publish-ready</p>
                    </div>
                  </div>
                </div>
                <h4 className="text-white font-bold mb-2">{fullOutputData.blogPost.metaTitle}</h4>
                <p className="text-xs text-neutral-500 italic mb-3">{fullOutputData.blogPost.metaDescription}</p>
                <p className="text-neutral-400 text-sm leading-relaxed line-clamp-4">{fullOutputData.blogPost.content}</p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {fullOutputData.blogPost.keywords.slice(0, 4).map(kw => (
                    <span key={kw} className="px-2 py-0.5 bg-neutral-800 rounded text-xs text-neutral-500">{kw}</span>
                  ))}
                </div>
              </motion.div>
            </div>
          </>
          )}

          {/* SOCIAL MEDIA TAB */}
          {activeTab === 'social' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Promo - Twitter */}
              <motion.div className="card-base p-5" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 bg-neutral-800 rounded flex items-center justify-center text-xs font-bold">X</div>
                  <span className="text-sm font-medium">Twitter/X Post</span>
                  <button onClick={() => handleCopy(fullOutputData.promoSnippets.twitter, 'twitter')} className="ml-auto flex items-center gap-1 px-2 py-1 bg-neutral-800 hover:bg-neutral-700 rounded text-xs text-neutral-400">
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
                <p className="text-neutral-300 text-sm whitespace-pre-line">{fullOutputData.promoSnippets.twitter}</p>
              </motion.div>

              {/* Promo - LinkedIn */}
              <motion.div className="card-base p-5" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 bg-[#0077B5] rounded flex items-center justify-center">
                    <Linkedin className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span className="text-sm font-medium">LinkedIn Post</span>
                  <button onClick={() => handleCopy(fullOutputData.promoSnippets.linkedin, 'linkedin')} className="ml-auto flex items-center gap-1 px-2 py-1 bg-neutral-800 hover:bg-neutral-700 rounded text-xs text-neutral-400">
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
                <p className="text-neutral-300 text-sm whitespace-pre-line line-clamp-6">{fullOutputData.promoSnippets.linkedin}</p>
              </motion.div>

              {/* Promo - Newsletter */}
              <motion.div className="card-base p-5" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 bg-purple-500/20 rounded flex items-center justify-center">
                    <Mail className="w-3.5 h-3.5 text-purple-400" />
                  </div>
                  <span className="text-sm font-medium">Newsletter Snippet</span>
                  <button onClick={() => handleCopy(fullOutputData.promoSnippets.newsletter, 'newsletter')} className="ml-auto flex items-center gap-1 px-2 py-1 bg-neutral-800 hover:bg-neutral-700 rounded text-xs text-neutral-400">
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
                <p className="text-neutral-300 text-sm whitespace-pre-line line-clamp-6">{fullOutputData.promoSnippets.newsletter}</p>
              </motion.div>

              {/* TikTok Caption — exact app format with copy buttons */}
              <div className="card-base p-6 md:col-span-2 lg:col-span-3">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-pink-500/10 flex items-center justify-center">
                    <svg className="w-5 h-5 text-pink-500" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/></svg>
                  </div>
                  <h3 className="font-semibold text-lg">TikTok Caption</h3>
                </div>
                <div className="space-y-4">
                  <div className="p-3 rounded-lg bg-neutral-900/50 border-l-2 border-pink-500">
                    <p className="text-xs text-pink-400 mb-1 font-medium">Hook (First Line)</p>
                    <p className="text-neutral-200">How to turn $10K into $1M 💸</p>
                    <button onClick={() => handleCopy("How to turn $10K into $1M 💸", 'tiktok-hook')} className="flex items-center gap-1 px-2 py-1 mt-2 bg-neutral-800 hover:bg-neutral-700 rounded text-xs text-neutral-400">
                      <Copy className="w-3 h-3" />
                      {copiedItem === 'tiktok-hook' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <div className="p-3 rounded-lg bg-neutral-900/50">
                    <p className="text-xs text-neutral-500 mb-1 font-medium">Main Caption</p>
                    <p className="text-neutral-200 text-sm whitespace-pre-line">Manish Pabrai&apos;s 3-step playbook:{'\n\n'}1. Keep your day job. Save aggressively.{'\n'}2. Park the default in Berkshire Hathaway.{'\n'}3. Wait for the rare idea that hits you over the head — and size up.{'\n\n'}No stock-picking. No trading. Just patience.</p>
                    <button onClick={() => handleCopy("Manish Pabrai's 3-step playbook:\n\n1. Keep your day job. Save aggressively.\n2. Park the default in Berkshire Hathaway.\n3. Wait for the rare idea that hits you over the head — and size up.\n\nNo stock-picking. No trading. Just patience.", 'tiktok-main')} className="flex items-center gap-1 px-2 py-1 mt-2 bg-neutral-800 hover:bg-neutral-700 rounded text-xs text-neutral-400">
                      <Copy className="w-3 h-3" />
                      {copiedItem === 'tiktok-main' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <div className="p-3 rounded-lg bg-neutral-900/50">
                    <p className="text-xs text-neutral-500 mb-2 font-medium">Hashtags</p>
                    <p className="text-sm text-blue-400">#investing #stocks #warrenbuffett #manishpabrai #myfirstmillion #wealthbuilding</p>
                    <button onClick={() => handleCopy("#investing #stocks #warrenbuffett #manishpabrai #myfirstmillion #wealthbuilding", 'tiktok-hashtags')} className="flex items-center gap-1 px-2 py-1 mt-2 bg-neutral-800 hover:bg-neutral-700 rounded text-xs text-neutral-400">
                      <Copy className="w-3 h-3" />
                      {copiedItem === 'tiktok-hashtags' ? 'Copied!' : 'Copy Hashtags'}
                    </button>
                  </div>
                  <div className="p-3 rounded-lg bg-neutral-900/50">
                    <p className="text-xs text-neutral-500 mb-1 font-medium">Call to Action</p>
                    <p className="text-neutral-200 text-sm">Save this before your next trade 🚀</p>
                  </div>
                  <button onClick={() => handleCopy("How to turn $10K into $1M 💸\n\nManish Pabrai's 3-step playbook:\n\n1. Keep your day job. Save aggressively.\n2. Park the default in Berkshire Hathaway.\n3. Wait for the rare idea that hits you over the head — and size up.\n\nNo stock-picking. No trading. Just patience.\n\n#investing #stocks #warrenbuffett #manishpabrai #myfirstmillion #wealthbuilding\n\nSave this before your next trade 🚀", 'tiktok-full')} className="w-full py-3 rounded-xl border border-neutral-700 hover:border-neutral-600 font-medium text-neutral-300 flex items-center justify-center gap-2">
                    <Copy className="w-4 h-4" />
                    {copiedItem === 'tiktok-full' ? 'Copied Full Caption!' : 'Copy Full Caption'}
                  </button>
                </div>
              </div>

              {/* Instagram Captions — exact app format with copy buttons */}
              <div className="card-base p-6 md:col-span-2 lg:col-span-3">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
                    <Instagram className="w-5 h-5 text-pink-400" />
                  </div>
                  <h3 className="font-semibold text-lg">Instagram Captions</h3>
                </div>
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-neutral-900/50 border-l-2 border-purple-500">
                    <p className="text-xs text-purple-400 mb-2 font-medium">Carousel Caption</p>
                    <p className="text-neutral-200 text-sm whitespace-pre-line">Manish Pabrai on growing $10K into $1M:{'\n\n'}&ldquo;We don&apos;t need to know many things about many things. We need to know a lot about a little.&rdquo;{'\n\n'}→ Swipe for the full 3-step playbook every patient investor should know</p>
                    <button onClick={() => handleCopy("Manish Pabrai on growing $10K into $1M:\n\n\"We don't need to know many things about many things. We need to know a lot about a little.\"\n\n→ Swipe for the full 3-step playbook every patient investor should know", 'ig-carousel')} className="flex items-center gap-1 px-2 py-1 mt-3 bg-neutral-800 hover:bg-neutral-700 rounded text-xs text-neutral-400">
                      <Copy className="w-3 h-3" />
                      {copiedItem === 'ig-carousel' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <div className="p-4 rounded-lg bg-neutral-900/50 border-l-2 border-pink-500">
                    <p className="text-xs text-pink-400 mb-2 font-medium">Story</p>
                    <p className="text-neutral-200 text-sm">$10K → $1M? 💸{'\n'}Manish Pabrai&apos;s playbook is wild.</p>
                    <button onClick={() => handleCopy("$10K → $1M? 💸\nManish Pabrai's playbook is wild.", 'ig-story')} className="flex items-center gap-1 px-2 py-1 mt-3 bg-neutral-800 hover:bg-neutral-700 rounded text-xs text-neutral-400">
                      <Copy className="w-3 h-3" />
                      {copiedItem === 'ig-story' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <div className="p-4 rounded-lg bg-neutral-900/50 border-l-2 border-orange-500">
                    <p className="text-xs text-orange-400 mb-2 font-medium">Reel Caption</p>
                    <p className="text-neutral-200 text-sm whitespace-pre-line">Buffett&apos;s most underrated question: &ldquo;And then what?&rdquo;{'\n\n'}It&apos;s the difference between a good investor and a great one. Listen to Manish Pabrai break it down.</p>
                    <button onClick={() => handleCopy("Buffett's most underrated question: \"And then what?\"\n\nIt's the difference between a good investor and a great one. Listen to Manish Pabrai break it down.", 'ig-reel')} className="flex items-center gap-1 px-2 py-1 mt-3 bg-neutral-800 hover:bg-neutral-700 rounded text-xs text-neutral-400">
                      <Copy className="w-3 h-3" />
                      {copiedItem === 'ig-reel' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <div className="p-3 rounded-lg bg-neutral-900/50">
                    <p className="text-xs text-neutral-500 mb-2 font-medium">Hashtags</p>
                    <p className="text-sm text-blue-400">#investing #valueinvesting #warrenbuffett #manishpabrai #wealthbuilding #financialfreedom</p>
                    <button onClick={() => handleCopy("#investing #valueinvesting #warrenbuffett #manishpabrai #wealthbuilding #financialfreedom", 'ig-hashtags')} className="flex items-center gap-1 px-2 py-1 mt-2 bg-neutral-800 hover:bg-neutral-700 rounded text-xs text-neutral-400">
                      <Copy className="w-3 h-3" />
                      {copiedItem === 'ig-hashtags' ? 'Copied!' : 'Copy Hashtags'}
                    </button>
                  </div>
                  <div className="p-3 rounded-lg bg-neutral-900/50">
                    <p className="text-xs text-neutral-500 mb-1 font-medium">Bio Link CTA</p>
                    <p className="text-neutral-200 text-sm">Listen to the full episode 🎧</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SEO & QUOTES TAB */}
          {activeTab === 'seo' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Resources Mentioned */}
              <motion.div className="card-base p-6 lg:col-span-2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Resources & Guest Info</h3>
                    <p className="text-xs text-neutral-500">Extracted from show notes</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-neutral-500 uppercase tracking-wide mb-2">Resources Mentioned</p>
                    <ul className="space-y-1.5">
                      {fullOutputData.showNotes.resourcesMentioned.map((r, i) => (
                        <li key={i} className="text-neutral-300 text-sm flex items-start gap-2">
                          <span className="text-blue-500">•</span> {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500 uppercase tracking-wide mb-2">Guest Bio</p>
                    {fullOutputData.showNotes.guestBios.map((g, i) => (
                      <p key={i} className="text-neutral-300 text-sm">{g}</p>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Best Quotes — exact app format with copy buttons */}
              <div className="card-base p-6 lg:col-span-2">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                    <Quote className="w-5 h-5 text-amber-500" />
                  </div>
                  <h3 className="font-semibold text-lg">Best Quotes</h3>
                </div>
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-neutral-900/50 border-l-2 border-primary">
                    <p className="text-neutral-200 italic">
                      &ldquo;We don&apos;t need to know many things about many things. We need to know a lot about a little.&rdquo;
                    </p>
                    <p className="text-sm text-primary mt-3">— Manish Pabrai</p>
                    <button onClick={() => handleCopy("We don't need to know many things about many things. We need to know a lot about a little.", 'quote1')} className="flex items-center gap-1 px-2 py-1 mt-3 bg-neutral-800 hover:bg-neutral-700 rounded text-xs text-neutral-400">
                      <Copy className="w-3 h-3" />
                      {copiedItem === 'quote1' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <div className="p-4 rounded-lg bg-neutral-900/50 border-l-2 border-primary">
                    <p className="text-neutral-200 italic">
                      &ldquo;Usually the best ideas, when you finally figure that out, they&apos;re very simple. You should be able to explain your thesis in four or five sentences to a 10-year-old.&rdquo;
                    </p>
                    <p className="text-sm text-primary mt-3">— Manish Pabrai</p>
                    <button onClick={() => handleCopy("Usually the best ideas, when you finally figure that out, they're very simple. You should be able to explain your thesis in four or five sentences to a 10-year-old.", 'quote2')} className="flex items-center gap-1 px-2 py-1 mt-3 bg-neutral-800 hover:bg-neutral-700 rounded text-xs text-neutral-400">
                      <Copy className="w-3 h-3" />
                      {copiedItem === 'quote2' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <div className="p-4 rounded-lg bg-neutral-900/50 border-l-2 border-primary">
                    <p className="text-neutral-200 italic">
                      &ldquo;The most important question to ask is: and then what?&rdquo;
                    </p>
                    <p className="text-xs text-neutral-500 mt-3">Manish Pabrai, quoting Warren Buffett</p>
                    <button onClick={() => handleCopy("The most important question to ask is: and then what?", 'quote3')} className="flex items-center gap-1 px-2 py-1 mt-3 bg-neutral-800 hover:bg-neutral-700 rounded text-xs text-neutral-400">
                      <Copy className="w-3 h-3" />
                      {copiedItem === 'quote3' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              </div>

              {/* SEO Title Suggestions — exact app format with copy buttons */}
              <div className="card-base p-6 lg:col-span-2">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                    <Search className="w-5 h-5 text-emerald-500" />
                  </div>
                  <h3 className="font-semibold text-lg">SEO Title Suggestions</h3>
                </div>
                <div className="space-y-2">
                  {[
                    "How to Turn $10K Into $1M — Manish Pabrai's Full Playbook",
                    "The 'And Then What?' Question That Made Buffett Billions",
                    "Why Concentrated Knowledge Beats Diversification",
                    "Manish Pabrai on Berkshire Hathaway as a Personal Index",
                    "Patience and Anomalies: The Real Path to 100x Returns"
                  ].map((title, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-neutral-900/50">
                      <span className="text-neutral-200">{title}</span>
                      <button onClick={() => handleCopy(title, `seo-title-${i}`)} className="flex items-center gap-1 px-2 py-1 bg-neutral-800 hover:bg-neutral-700 rounded text-xs text-neutral-400">
                        <Copy className="w-3 h-3" />
                        {copiedItem === `seo-title-${i}` ? 'Copied!' : ''}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* YOUTUBE TAB */}
          {activeTab === 'youtube' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* YouTube Description */}
              <motion.div className="card-base p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Video Description</h3>
                    <p className="text-xs text-neutral-500">SEO-optimized for YouTube</p>
                  </div>
                  <span className="ml-auto text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">Pro</span>
                </div>
                <div className="bg-neutral-900/50 rounded-lg p-4 text-sm text-neutral-300 space-y-2">
                  <p className="text-white font-medium">🎯 Unlock the secret to growing $10K into $1M — with investor Manish Pabrai.</p>
                  <p>Sean sits down with Manish Pabrai to break down the patient-investor playbook: concentrated knowledge, market anomalies, and the &ldquo;and then what?&rdquo; question.</p>
                  <p className="text-neutral-400">📌 Key Takeaways:</p>
                  <ul className="text-neutral-400 text-xs space-y-1 ml-4">
                    <li>• Treat Berkshire Hathaway as a personal index</li>
                    <li>• Wait for the rare anomaly — then size up</li>
                    <li>• Explain every thesis in 4–5 sentences</li>
                  </ul>
                </div>
              </motion.div>

              {/* YouTube Chapters */}
              <motion.div className="card-base p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold">YouTube Chapters</h3>
                    <p className="text-xs text-neutral-500">Timestamp navigation</p>
                  </div>
                  <span className="ml-auto text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">Pro</span>
                </div>
                <div className="bg-neutral-900/50 rounded-lg p-4 text-sm font-mono text-neutral-300 space-y-1">
                  <p>00:00 Transforming $10K into $1M</p>
                  <p>08:40 First &amp; Second Order Thinking</p>
                  <p>17:21 Discovering Investment Anomalies</p>
                  <p>26:02 Simplicity in Investing</p>
                  <p>34:42 The Narrow Circle of Competence</p>
                  <p>52:04 Dakshina Foundation</p>
                  <p>1:09:24 Rakesh Jhunjhunwala&apos;s Strategy</p>
                </div>
              </motion.div>

              {/* Pinned Comment */}
              <motion.div className="card-base p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                    <Quote className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Pinned Comment</h3>
                    <p className="text-xs text-neutral-500">Engagement booster</p>
                  </div>
                  <span className="ml-auto text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">Pro</span>
                </div>
                <div className="bg-neutral-900/50 rounded-lg p-4 text-sm text-neutral-300">
                  <p>What&apos;s your boldest investment idea to turn $10,000 into $1 million? 🚀 Drop a comment below and let&apos;s talk strategy. Subscribe for more deep-dive episodes with the world&apos;s best investors.</p>
                </div>
              </motion.div>

              {/* YouTube SEO Titles (NEW) */}
              <motion.div className="card-base p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                    <Search className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold">YouTube SEO Titles</h3>
                    <p className="text-xs text-neutral-500">10 optimized titles</p>
                  </div>
                  <span className="ml-auto text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">Pro</span>
                </div>
                <div className="space-y-2">
                  {['How to Turn $10K Into $1M — Manish Pabrai', "The Buffett Question 99% of Investors Miss", 'Why Berkshire Hathaway Beats the S&P', 'Patience: The Skill That Compounds Most'].map((title, i) => (
                    <div key={i} className="bg-neutral-900/50 rounded-lg px-3 py-2 text-sm text-neutral-300">{title}</div>
                  ))}
                </div>
              </motion.div>

              {/* Shorts Hooks */}
              <motion.div className="card-base p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                    <Video className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Shorts Hooks</h3>
                    <p className="text-xs text-neutral-500">8-10 viral titles</p>
                  </div>
                  <span className="ml-auto text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">Pro</span>
                </div>
                <div className="space-y-2">
                  {['Can $10K really become $1M?', "Buffett's overlooked secret", 'Stop spreading too thin', 'Why patience beats trading', 'The 2x4 anomaly rule', 'Explain it to a 10-year-old'].map((hook, i) => (
                    <div key={i} className="bg-neutral-900/50 rounded-lg px-3 py-2 text-sm text-neutral-300">{hook}</div>
                  ))}
                </div>
              </motion.div>

              {/* SRT Subtitles */}
              <motion.div className="card-base p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                    <Download className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Subtitles (SRT)</h3>
                    <p className="text-xs text-neutral-500">Download & upload to YouTube</p>
                  </div>
                  <span className="ml-auto text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">Pro</span>
                </div>
                <div className="bg-neutral-900/50 rounded-lg p-4 text-xs font-mono text-neutral-400">
                  <p>1</p>
                  <p>{'00:00:00,000 --> 00:00:04,400'}</p>
                  <p className="text-neutral-300">How would I take 10K and turn it into a million?</p>
                  <p className="mt-2">2</p>
                  <p>{'00:00:11,000 --> 00:00:18,400'}</p>
                  <p className="text-neutral-300">We don&apos;t need to know many things about many things. We need to know a lot about a little.</p>
                </div>
              </motion.div>
            </div>
          )}

          {/* AI CONTROLS TAB */}
          {activeTab === 'controls' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Tone Selection */}
              <motion.div className="card-base p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                    <Sliders className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Tone Selection</h3>
                    <p className="text-xs text-neutral-500">Match your brand voice</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {fullOutputData.toneOptions.map((tone, i) => (
                    <span key={tone} className={`px-3 py-1.5 rounded-lg text-sm ${i === 0 ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' : 'bg-neutral-800 text-neutral-400'}`}>
                      {tone}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* Custom Instructions */}
              <motion.div className="card-base p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                    <PenTool className="w-5 h-5 text-cyan-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Custom Instructions</h3>
                    <p className="text-xs text-neutral-500">Guide the AI output</p>
                  </div>
                  <span className="ml-auto text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">Pro</span>
                </div>
                <div className="bg-neutral-800/50 rounded-lg p-3">
                  <p className="text-neutral-400 text-sm italic">&ldquo;{fullOutputData.customInstructions}&rdquo;</p>
                </div>
              </motion.div>

              {/* Export */}
              <motion.div className="card-base p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                    <FileArchive className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold">ZIP Export</h3>
                    <p className="text-xs text-neutral-500">Download all assets</p>
                  </div>
                  <span className="ml-auto text-xs text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded">Basic+</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {fullOutputData.exportFiles.slice(0, 6).map(file => (
                    <div key={file} className="flex items-center gap-2 px-2 py-1.5 bg-neutral-800/50 rounded text-xs">
                      <FileText className="w-3 h-3 text-neutral-500" />
                      <span className="text-neutral-400 truncate">{file}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          )}

          {/* Output Summary CTA */}
          <motion.div 
            className="mt-16 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex flex-wrap justify-center gap-3 mb-6">
              <span className="px-3 py-1 bg-neutral-800 rounded-full text-xs text-neutral-400">Transcript</span>
              <span className="px-3 py-1 bg-neutral-800 rounded-full text-xs text-neutral-400">Show Notes</span>
              <span className="px-3 py-1 bg-neutral-800 rounded-full text-xs text-neutral-400">Chapters</span>
              <span className="px-3 py-1 bg-neutral-800 rounded-full text-xs text-neutral-400">Blog Post</span>
              <span className="px-3 py-1 bg-neutral-800 rounded-full text-xs text-neutral-400">Twitter</span>
              <span className="px-3 py-1 bg-neutral-800 rounded-full text-xs text-neutral-400">LinkedIn</span>
              <span className="px-3 py-1 bg-neutral-800 rounded-full text-xs text-neutral-400">Newsletter</span>
              <span className="px-3 py-1 bg-primary/20 rounded-full text-xs text-primary">TikTok</span>
              <span className="px-3 py-1 bg-primary/20 rounded-full text-xs text-primary">Instagram</span>
              <span className="px-3 py-1 bg-primary/20 rounded-full text-xs text-primary">Quotes</span>
              <span className="px-3 py-1 bg-primary/20 rounded-full text-xs text-primary">SEO Titles</span>
              <span className="px-3 py-1 bg-blue-500/20 rounded-full text-xs text-blue-400">ZIP Export</span>
            </div>
            <p className="text-neutral-400 mb-2 text-lg">
              All of this. From one audio file. In under 5 minutes.
            </p>
            <p className="text-neutral-500 mb-8 text-sm">
              Free plan includes core outputs. Pro unlocks everything.
            </p>
            <Link to="/register">
              <Button className="btn-primary flex items-center gap-2 mx-auto text-lg px-8 py-6 font-semibold" data-testid="output-cta-btn">
                Try Your First Episode Free
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>

            {/* REAL OUTPUT EXAMPLES — proof layer */}
            <RealOutputExamplesSection />

            {/* Why this matters — minimal centered closing block */}
            <motion.div
              className="text-center max-w-2xl mx-auto mt-20 md:mt-24 mb-8 px-4"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              data-testid="why-this-matters"
            >
              <p className="text-xl md:text-2xl font-semibold text-white leading-snug">
                This is not just content.<br />
                This is how you grow.
              </p>
              <p className="uppercase text-sm tracking-widest text-neutral-400 mt-6 mb-4">
                Why this matters
              </p>
              <p className="text-base md:text-lg text-neutral-300 leading-relaxed">
                Rank on Google with every episode.<br />
                Get discovered on YouTube.<br />
                Stay consistent without burning out.<br />
                One episode becomes your entire content distribution system.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FROM THE BLOG — minimal SEO entry point */}
      <HomeBlogPreview />

      {/* Stats */}
      <section className="py-20 px-6 md:px-12 bg-[#0A0A0A]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  className="text-center py-6"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Icon className="w-5 h-5 mx-auto mb-3 text-neutral-600" strokeWidth={1.5} />
                  <div className="text-3xl md:text-4xl font-unbounded font-bold text-primary mb-1">
                    {stat.value}
                  </div>
                  <p className="text-neutral-500 text-xs">{stat.label}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6 md:px-12 bg-[#0A0A0A]" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-secondary uppercase text-xs tracking-widest mb-4 font-semibold">Dead Simple</p>
            <h2 className="text-3xl md:text-4xl font-unbounded font-bold mb-4">Three Steps. Five Minutes.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { step: '01', title: 'Upload', desc: 'Drop your MP3, WAV, M4A, FLAC, OGG, MP4, or MOV file. Up to 120 minutes.' },
              { step: '02', title: 'Generate', desc: 'AI transcribes and creates all 12+ content assets.' },
              { step: '03', title: 'Publish', desc: 'Copy, download ZIP, or export. Ready to use.' }
            ].map((item, index) => (
              <motion.div key={item.step} className="text-center" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.15 }}>
                <div className="text-7xl font-unbounded font-bold text-neutral-800/80 mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-neutral-400">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6 md:px-12 bg-[#0A0A0A]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <div className="card-highlight p-12 md:p-16 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-secondary/10" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-unbounded font-bold mb-4">Ready to turn your next episode into publish-ready content?</h2>
              <p className="text-neutral-400 mb-8 max-w-xl mx-auto text-lg">One episode. 12+ content assets. Under five minutes.</p>
              <Link to="/register">
                <Button className="btn-primary text-lg px-12 py-6 font-semibold shadow-lg shadow-primary/30 hover:shadow-primary/50 transition-shadow" data-testid="final-cta-btn">
                  Try Your First Episode Free
                </Button>
              </Link>
              <p className="text-neutral-500 text-sm mt-6">No credit card required • 60 minutes included • Cancel anytime</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;
